// Rapport de mission saisi sur le terrain (avant, après ou final) : constats, travaux, matériaux,
// durée, photos, position jointe si l'appareil la donne. Enregistré sur l'appareil dans la file
// (une opération rapport_mission du contrat schemaChargeRapportMission, avec la version de la mission
// connue de l'appareil, D-130), synchronisé ensuite. Brouillon des champs texte conservé (H9-03).
import { schemaChargeRapportMission, type ChargeRapportMission, type MissionTerrain, type TypeRapportMission as TypeRapport } from '@cc/contrats';
import { Bouton, CaseACocher, Champ, Formulaire, Selection, ZoneTexte, useBrouillon, type ErreurChamp } from '@cc/ui';
import { useState } from 'react';
import { messageErreur } from '../../api/erreurs';
import { positionContrat } from '../../terrain/appareil';
import { lireMateriaux } from '../../terrain/charges';
import { LIBELLES_TYPE_RAPPORT } from '../../terrain/etat-local';
import { uuidV7 } from '../../terrain/identifiants';
import { releverPosition } from '../../terrain/position';
import type { OperationLocale } from '../../terrain/stockage';
import { synchroniseurPartage } from '../../terrain/synchronisation';
import { SaisiePhotos, versPhotosAEnregistrer, type PhotoSaisie } from './SaisiePhotos';
import styles from './terrain.module.css';

interface Valeurs {
  type: TypeRapport;
  constats: string;
  travaux: string;
  materiaux: string;
  duree: string;
}

const VIDE: Valeurs = { type: 'apres', constats: '', travaux: '', materiaux: '', duree: '' };

export function FormulaireRapport({ mission, typeInitial = 'apres', onEnregistre }: { mission: MissionTerrain; typeInitial?: TypeRapport; onEnregistre?: (o: OperationLocale) => void }) {
  const [valeurs, setValeurs] = useState<Valeurs>({ ...VIDE, type: typeInitial });
  const [photos, setPhotos] = useState<PhotoSaisie[]>([]);
  const [joindrePosition, setJoindrePosition] = useState(true);
  const [erreurs, setErreurs] = useState<ErreurChamp[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const brouillon = useBrouillon<Valeurs>(`terrain-rapport-${mission.id}`, valeurs);
  const prefixe = `rapport-${mission.id}`;
  const changer = (partiel: Partial<Valeurs>) => setValeurs((v) => ({ ...v, ...partiel }));

  const soumettre = async () => {
    const fautes: ErreurChamp[] = [];
    if (!valeurs.constats.trim() && !valeurs.travaux.trim()) fautes.push({ champ: `${prefixe}-constats`, message: 'Décrivez au moins les constats ou les travaux' });
    const duree = valeurs.duree.trim() === '' ? null : Number(valeurs.duree);
    if (duree !== null && (!Number.isInteger(duree) || duree < 0 || duree > 1440)) fautes.push({ champ: `${prefixe}-duree`, message: 'La durée est un nombre de minutes entre 0 et 1440' });
    setErreurs(fautes);
    if (fautes.length) return;
    setEnCours(true);
    setErreur(null);
    setMessage(null);
    try {
      const position = joindrePosition ? (await releverPosition(8_000)).position : null;
      // Charge contrôlée par le schéma du contrat avant l'enregistrement : le serveur la recevra telle quelle.
      const charge: ChargeRapportMission = schemaChargeRapportMission.parse({
        type: valeurs.type,
        contenu: { constats: valeurs.constats.trim(), travaux: valeurs.travaux.trim(), materiaux: lireMateriaux(valeurs.materiaux), ...(duree === null ? {} : { duree_minutes: duree }), termine: valeurs.type === 'final' },
        photos: photos.map((p) => p.id_client),
        ...(position ? { position: positionContrat(position) } : {}),
      });
      const op = await synchroniseurPartage().enfiler(
        { id_client: uuidV7(), type: 'rapport_mission', objet_type: 'mission', objet_id: mission.id, version_attendue: mission.version, enregistre_le: new Date().toISOString(), charge },
        { titre: LIBELLES_TYPE_RAPPORT[valeurs.type], objet_libelle: `${mission.intervention.numero} · ${mission.intervention.titre}`, photos: versPhotosAEnregistrer(photos, 'rapport_mission') },
      );
      brouillon.effacer();
      setValeurs({ ...VIDE, type: valeurs.type === 'avant' ? 'apres' : 'final' });
      setPhotos([]);
      setMessage(`${LIBELLES_TYPE_RAPPORT[valeurs.type]} enregistré sur cet appareil avec ${photos.length} photo(s)${position ? ' et votre position' : ''}.`);
      onEnregistre?.(op);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  const erreurDe = (champ: string) => erreurs.find((f) => f.champ === `${prefixe}-${champ}`)?.message ?? null;

  return (
    <Formulaire
      libelle="Nouveau rapport de mission"
      onSoumettre={() => void soumettre()}
      erreurs={erreurs}
      brouillonEnregistreLe={brouillon.enregistreLe}
      restauration={brouillon.brouillonDisponible ? { date: brouillon.brouillonDate, onReprendre: () => setValeurs({ ...VIDE, ...(brouillon.brouillonDisponible as Valeurs) }), onIgnorer: brouillon.ignorer } : null}
      actions={
        <Bouton type="submit" chargement={enCours} libelleChargement="Enregistrement sur l'appareil en cours">
          Enregistrer le rapport
        </Bouton>
      }
    >
      {erreur ? (
        <p role="alert" className={styles.aide}>
          {erreur}
        </p>
      ) : null}
      {message ? (
        <p role="status" className={styles.aide}>
          {message}
        </p>
      ) : null}
      <Selection id={`${prefixe}-type`} libelle="Type de rapport" options={(['avant', 'apres', 'final'] as const).map((t) => ({ valeur: t, libelle: LIBELLES_TYPE_RAPPORT[t] }))} value={valeurs.type} onChange={(e) => changer({ type: e.target.value as TypeRapport })} />
      <ZoneTexte id={`${prefixe}-constats`} libelle="Constats sur place" rows={3} value={valeurs.constats} onChange={(e) => changer({ constats: e.target.value })} erreur={erreurDe('constats')} aide="Ce que vous avez trouvé en arrivant ou en finissant." />
      <ZoneTexte id={`${prefixe}-travaux`} libelle="Travaux réalisés" rows={3} value={valeurs.travaux} onChange={(e) => changer({ travaux: e.target.value })} facultatif />
      <Champ id={`${prefixe}-materiaux`} libelle="Matériaux utilisés" value={valeurs.materiaux} onChange={(e) => changer({ materiaux: e.target.value })} aide="Séparés par des virgules, quantité en tête si vous la connaissez : 2 sacs de ciment, lampe 150 W." facultatif />
      <Champ id={`${prefixe}-duree`} libelle="Durée (minutes)" inputMode="numeric" value={valeurs.duree} onChange={(e) => changer({ duree: e.target.value })} erreur={erreurDe('duree')} facultatif />
      <SaisiePhotos id={`${prefixe}-photos`} photos={photos} onChange={setPhotos} aide="Réduites à 1 600 px sur l'appareil, envoyées une par une à la synchronisation." />
      <CaseACocher id={`${prefixe}-position`} libelle="Joindre ma position (si l'appareil l'autorise)" checked={joindrePosition} onChange={(e) => setJoindrePosition(e.target.checked)} />
    </Formulaire>
  );
}
